import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { confirmWorkspaceNavigation } from '../shell/workspaceNavigationGuard';
import styles from './ProfileDropdown.module.css';

const ProfileDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get initials from user name or email
  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return user?.email || t('profile.default_user');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleLogout = async () => {
    if (!(await confirmWorkspaceNavigation())) return;
    logout();
    navigate('/login');
  };

  const handleNavigation = async (path: string) => {
    if (!(await confirmWorkspaceNavigation())) return;
    setIsOpen(false);
    navigate(path);
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        className={styles.avatarButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className={styles.avatar}>
          {getInitials()}
        </div>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.userInfo}>
            <div className={styles.avatarLarge}>
              {getInitials()}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{getDisplayName()}</span>
              <span className={styles.userEmail}>{user?.email}</span>
            </div>
          </div>

          <div className={styles.divider} />

          <nav className={styles.menu}>
            <button
              className={styles.menuItem}
              onClick={() => handleNavigation('/')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 6L8 1.5L14 6V13.5C14 14.0523 13.5523 14.5 13 14.5H3C2.44772 14.5 2 14.0523 2 13.5V6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 14.5V8H10V14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('profile.dashboard')}
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigation('/profile')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M3 14.5C3 11.7386 5.23858 9.5 8 9.5C10.7614 9.5 13 11.7386 13 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {t('profile.profile')}
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigation('/progress')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M8 5V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('profile.my_progress')}
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigation('/credentials')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M5.5 9L4.5 14.5L8 12.5L11.5 14.5L10.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('profile.my_certificates', 'My Certificates')}
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigation('/settings')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M13.5 8C13.5 7.65 13.47 7.31 13.41 6.98L14.89 5.82C15.02 5.72 15.06 5.54 14.97 5.39L13.57 3.11C13.48 2.96 13.31 2.9 13.14 2.96L11.39 3.66C10.91 3.29 10.37 3 9.79 2.8L9.5 0.95C9.47 0.78 9.32 0.65 9.15 0.65H6.35C6.18 0.65 6.03 0.78 6 0.95L5.71 2.8C5.13 3 4.59 3.29 4.11 3.66L2.36 2.96C2.19 2.9 2.02 2.96 1.93 3.11L0.53 5.39C0.44 5.54 0.48 5.72 0.61 5.82L2.09 6.98C2.03 7.31 2 7.65 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {t('profile.settings')}
            </button>
          </nav>

          <div className={styles.divider} />

          <button
            className={`${styles.menuItem} ${styles.logoutItem}`}
            onClick={handleLogout}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M11 11L14 8L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {t('profile.signout')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
